<template>
  <div class="gs-checkout-builder-step gs-checkout-links-step">
    <nav class="gs-checkout-links-step__nav" aria-label="Checkout link formats">
      <button
        v-for="tab in tabs"
        :key="tab.value"
        type="button"
        :class="{ 'is-active': currentTab === tab.value }"
        :aria-current="currentTab === tab.value ? 'page' : null"
        @click="handleTabChange(tab.value)"
      >
        <strong>{{ tab.title }}</strong>
        <span>{{ tab.description }}</span>
      </button>
    </nav>
    <section class="gs-checkout-links-step__content">
      <header>
        <p>Share checkout</p>
        <h3>{{ currentTabMeta.title }}</h3>
        <span>{{ currentTabMeta.description }}</span>
      </header>
      <div class="gs-checkout-links-step__panel">
        <tab-link v-if="currentTab == 'link'" :checkout_link="checkout_link" :onCopy="onCopy" :onError="onError" />
        <tab-embed v-if="currentTab == 'embed'" :checkout_embed="checkout_embed" :onCopy="onCopy" :onError="onError" />
        <tab-popup v-if="currentTab == 'popup'" />
      </div>
    </section>
  </div>
</template>
<script>
import CheckoutLink from '@/models/CheckoutLink';
import Product from '@/models/Product';
import Notify from '@groovepages/gd-ui-kit/notify';

import TabLink from './tabs/TabLink';
import TabEmbed from './tabs/TabEmbed';
import TabPopup from './tabs/TabPopup';


export default {
  name: 'LinksStep',

  components: {
    TabLink,
    TabEmbed,
    TabPopup
  },

  props: {
    model: {
      required: true,
    },
    selectPricePoint: {
      required: true,
    },
    prductDetails: {
      required: true,
    },
    selectCheckoutStepType: {
      required: true,
    },
    checkoutLinks: {
      required: true,
    },
  },

  watch: {
    checkoutLinksLocal() {
      this.$emit('updateCheckoutProp', this.checkoutLinksLocal);
    },
  },

  data() {
    return {
      currentTab: 'link',
      tabs: [
        {
          title: this.$t('frontend_product_hosted_checkout'),
          value: 'link',
          description: 'Copy the hosted checkout URL.',
        },
        {
          title: this.$t('frontend_product_embedded_into'),
          value: 'embed',
          description: 'Add the checkout to another site.',
        },
        {
          title: this.$t('frontend_groovesell_link_checkout_popup_modal'),
          value: 'popup',
          description: 'Open checkout from a GroovePages button.',
        },
      ],
      localModel: this.model,
      checkout_link: null,
      checkout_embed: null,
      selectedProduct: null,
      checkoutLinksLocal: [],
    };
  },

  computed: {
    currentTabMeta() {
      return this.tabs.find((tab) => tab.value === this.currentTab) || this.tabs[0];
    },
  },

  methods: {
    onCopy() {
      Notify.success(this.$t('frontend_copy_link'));
    },

    onError() {
      Notify.fail(this.$t('frontend_copy_link_fail'));
    },

    handleTabChange(newTab) {
      this.currentTab = newTab;
    },
  },

  async created() {
    if (this.model.isEdit) {
      this.$loader.start('editFetch');
      const product = await Product.$find(this.$route.params.product_id);
      const links = await product.checkoutLinks().$find(this.model.id);
      this.checkout_link = links.checkout_link;
      this.checkout_embed = links.checkout_embed;
      this.$loader.end('editFetch');
      return;
    }

    this.$loader.start('createdFetch');
    const skipFieldById = new Map(
      (this.localModel.skip_field_arr || [])
        .filter(entry => entry && entry.id != null)
        .map(entry => [String(entry.id), entry]),
    );
    const anyEnabled = this.selectPricePoint.some(pricePoint => pricePoint.enabled);
    const enabledPricingOptionsArray = this.selectPricePoint
      .filter(pricePoint => (anyEnabled ? pricePoint.enabled : true))
      .map((pricePoint) => {
        const skip = skipFieldById.get(String(pricePoint.id)) || {};
        return {
          id: pricePoint.id,
          enabled: true,
          ask_username: !!skip.ask_username,
          ask_for_password: !!skip.ask_for_password,
          ask_for_password_end: !!skip.ask_for_password_end,
          skip_billing_info: !!skip.skip_billing_info,
          skip_payment_info: !!skip.skip_payment_info,
          skip_terms_or_service: !!skip.skip_terms_or_service,
          skip_phone: !!skip.skip_phone,
        };
      });

    let checkout_link_page_options = {};
    checkout_link_page_options = {
      look_and_feel: this.prductDetails.checkout_page_options.look_and_feel,
      product_image: this.prductDetails.checkout_page_options.product_image,
      tracking_codes: this.prductDetails.checkout_page_options.tracking_codes,
    };

    const newMultiPricePointLink = new CheckoutLink({
      product_id: this.prductDetails.id,
      price_point_id: 0,
      name: this.localModel.name,
      pricing_options: enabledPricingOptionsArray,
      is_onestep: this.selectCheckoutStepType,
      checkout_link_page_options,
      force_affiliate: this.localModel.force_affiliate,
      force_affiliate_id: this.localModel.force_affiliate_id,
    });

    const savedCheckoutLinkData = await newMultiPricePointLink.save();
    this.multiPricePointLink = await this.prductDetails.checkoutLinks().$find(savedCheckoutLinkData.data.id);
    this.checkout_link = this.multiPricePointLink.checkout_link;
    this.checkout_embed = this.multiPricePointLink.checkout_embed;

    this.selectedProduct = await Product.$find(this.$route.params.product_id);
    this.checkoutLinksLocal = await this.selectedProduct.checkoutLinks().$get();
    this.$loader.end('createdFetch');
  },
};
</script>

<style scoped>
.gs-checkout-links-step {
  display: grid;
  min-height: 430px;
  overflow: hidden;
  background: #fff;
  border: 1px solid var(--gs-border, #e2e8f0);
  border-radius: 12px;
  grid-template-columns: 280px minmax(0, 1fr);
}

.gs-checkout-links-step__nav {
  display: grid;
  align-content: start;
  gap: 6px;
  padding: 18px;
  background: #f8fafc;
  border-right: 1px solid var(--gs-border, #e2e8f0);
}

.gs-checkout-links-step__nav button {
  width: 100%;
  padding: 12px;
  color: #64748b;
  text-align: left;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 9px;
}

.gs-checkout-links-step__nav button:hover,
.gs-checkout-links-step__nav button:focus-visible {
  color: #1d4f91;
  background: #f1f7ff;
  border-color: #dbeafe;
  outline: none;
}

.gs-checkout-links-step__nav button.is-active {
  color: #1d4f91;
  background: var(--gs-active-soft, #eff6ff);
  border-color: var(--gs-active-border, #93c5fd);
}

.gs-checkout-links-step__nav strong,
.gs-checkout-links-step__nav span {
  display: block;
}

.gs-checkout-links-step__nav strong {
  font-size: 12px;
  font-weight: 800;
}

.gs-checkout-links-step__nav span {
  margin-top: 3px;
  font-size: 10px;
  line-height: 1.4;
}

.gs-checkout-links-step__content {
  min-width: 0;
  padding: 24px;
}

.gs-checkout-links-step__content header {
  padding-bottom: 18px;
  border-bottom: 1px solid var(--gs-border, #e2e8f0);
}

.gs-checkout-links-step__content header p,
.gs-checkout-links-step__content header h3 {
  margin: 0;
}

.gs-checkout-links-step__content header p {
  color: var(--gs-active, #2563eb);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .07em;
  text-transform: uppercase;
}

.gs-checkout-links-step__content header h3 {
  margin-top: 5px;
  color: var(--gs-foreground, #182230);
  font-size: 17px;
  font-weight: 800;
}

.gs-checkout-links-step__content header span {
  display: block;
  margin-top: 5px;
  color: var(--gs-muted-foreground, #667085);
  font-size: 11px;
}

.gs-checkout-links-step__panel {
  max-width: 680px;
  padding-top: 22px;
}
</style>
